import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { finalize } from 'rxjs/operators';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { translate } from 'components/Translation';
import { useHistory } from 'react-router-dom';
import { AccountsService } from 'services/accounts/accounts';
import { useDispatch, useSelector } from 'react-redux';
import { showLoadingIndicator, hideLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { PaymentService } from 'services/payments/payment';
import _ from 'lodash';
import Payment from './tabs/Payment';
import PaymentFrame from 'components/PaymentFrame/PaymentFrame';
import { decode } from 'utils/crypto/crypto';
import { setTopup } from 'redux/modules/topup/actions';
import { topUpValueSelector, selectedNumbersSelector } from 'redux/modules/topup/selectors';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import NavigationPromptDialog from './subcomponents/NavigationPrompt';
import { SingleAccount } from 'utils/api/models/accounts/singleAccount';
import TabsWithDots from 'components/TabsWithDots/TabsWithDots';
import NumbersValue from './tabs/NumbersValue';
import Donation from './tabs/Donation';
import PaymentConfirmationDialog from './subcomponents/PaymentConfirmationDialog';
import PaymentRejectedDialog from './subcomponents/PaymentRejectedDialog';
import { setToast } from 'redux/modules/toast/actions';
import { IPaymentToken } from 'utils/api/models/payments/paymentTokens';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import { isSmartPay } from 'utils/killswitch';
import { PaymentRedirection } from 'components/PaymentMethod/helper/payment';
import { setPaymentType } from 'redux/modules/paymentType/actions';

type DialogType = 'addNumbers' | 'paymentFrame' | null;

interface ITopUpProps {
	match: {
		params: {
			phoneNumberEncoded?: string;
		};
	};
}

interface ILoading {
	numbers: boolean;
	payment: boolean;
}

enum Tabs {
	NUMBERS_VALUE,
	DONATION,
	PAYMENT,
}

const DEFAULT_CARD: types.payment.IFormData = {
	number: '',
	cvv: '',
	date: '',
	holder: '',
	email: '',
	cardType: 'VISA',
	type: 'card',
	tokenId: '',
	saveCard: false,
};

const TopUp = ({ match }: ITopUpProps) => {
	const [showDonations, setShowDonations] = useState<boolean>(false);
	const [selectedNumbers, setSelectedNumbers] = useState<types.topUp.INumber[]>([]);
	const [activeTab, setActiveTab] = useState<Tabs>(Tabs.NUMBERS_VALUE);
	const [numbers, setNumbers] = useState<SingleAccount[]>([]);
	const [loading, setLoading] = useState<ILoading>({
		numbers: false,
		payment: false,
	});
	const [dialog, setDialog] = useState<DialogType>(null);
	const [topupValue, setTopupValue] = useState<number>(3);
	const [topUpError, setTopUpError] = useState<string | null>(null);
	const [checkValidity, setCheckValidity] = useState<boolean>(false);
	const [foundNumber, setFoundNumber] = useState<boolean | null>(false);
	const [paymentRejected, setPaymentRejected] = useState<boolean>(false);
	const [topUpLimit, setTopUpLimit] = useState<types.payment.paymentLimitReached>(null);
	const [formData, setFormData] = useState<types.payment.IFormData>(DEFAULT_CARD);
	const [selectedDonation, setSelectedDonation] = useState<types.payment.IDonation | null>(null);
	const { phoneNumberEncoded } = match.params;
	const history = useHistory();
	const dispatch = useDispatch();
	const topupRef = useRef<boolean>(false);
	const isSuccess: boolean = window.location.href.includes('success');
	const isError: boolean = window.location.href.includes('error') || window.location.href.includes('failure');
	const isConfirmationDialog: boolean = isSuccess || isError;
	const totalAmount: number = useMemo(
		() =>
			selectedNumbers.reduce(
				(previous, { topUpValue }) => (topUpValue == null ? 0 : Number(topUpValue)) + previous,
				0
			),
		[selectedNumbers]
	);
	const topUpValue = useSelector(topUpValueSelector) || totalAmount;
	const savedSelectedNumbers = useSelector(selectedNumbersSelector);
	const toggleSaveCard = () => setFormData((formData) => ({ ...formData, saveCard: !formData.saveCard }));
	const setSaveCard = (saveCard: boolean) => setFormData((formData) => ({ ...formData, saveCard }));
	const remoteConfig = useSelector(remoteConfigSelector);
	const DISABLE_SMART_PAY = !isSmartPay(remoteConfig);
	useEffect(() => {
		if (Object.values(loading).every((value) => !value)) {
			dispatch(hideLoadingIndicator());
		} else {
			dispatch(showLoadingIndicator());
		}
	}, [loading, dispatch]);

	useEffect(() => {
		if (phoneNumberEncoded) {
			if (numbers) {
				const phoneNumber = decode(phoneNumberEncoded);
				const foundNumber = numbers.find((number) => number.accountNum === phoneNumber);
				if (foundNumber) {
					setSelectedNumbers([{ ...foundNumber, topUpValue: topupValue }]);
				}
				setFoundNumber(true);
			}
		}
	}, [phoneNumberEncoded, numbers, topupValue]);

	const setFormCardData = (name: keyof types.payment.IFormData, value: string) =>
		setFormData((formData) => ({ ...formData, [name]: value }));

	const setToken = useCallback(
		(token: IPaymentToken) =>
			setFormData((formData) => ({
				...formData,
				tokenId: token.id,
				holder: `${token.firstName} ${token.lastName}`,
				cardType: token.cardType,
				number: token.cardNumber,
				type: 'token',
			})),
		[setFormData]
	);

	const dialogReset = () => {
		setFormData(DEFAULT_CARD);
		setCheckValidity(false);
		setDialog(null);
		setTopUpError(null);
		setTopUpLimit(null);
	};

	const hardReset = () => {
		setActiveTab(Tabs.NUMBERS_VALUE);
		setShowDonations(false);
		setSelectedDonation(null);
		dialogReset();
		setSelectedNumbers([]);
		setTopupValue(3);
		setFoundNumber(null);
	};

	const handleAddNumber = () => {
		setDialog('addNumbers');
	};

	const closeDialog = () => {
		setDialog(null);
	};

	const handleTopUp = (data: types.payment.MakePayment) => {
		setLoading((loading) => ({ ...loading, payment: true }));
		PaymentService.makePayment(data).subscribe(
			() => {
				history.push('?result=success');
				setActiveTab(Tabs.NUMBERS_VALUE);
				setDialog(null);
			},
			(error) => {
				if (error.response.code !== 'PAYER_AUTHENTICATION_REQUIRED') {
					setLoading((loading) => ({ ...loading, payment: false }));
				}
				switch (error.response.code) {
					case 'PAYER_AUTHENTICATION_REQUIRED':
						dispatch(setPaymentType({ action: 'TOP_UP' }));
						dispatch(
							setTopup({
								topupValue: totalAmount,
								selectedNumbers,
							})
						);

						PaymentRedirection(error?.response?.url);
						break;

					case 'CREDIT_CARD_PAYMENT_REQUEST_EXCEEDS_MAX_DONATION_AMOUNT':
						setTopUpLimit('credit');
						break;

					case 'DEBIT_CARD_PAYMENT_REQUEST_EXCEEDS_MAX_TOPUP_AMOUNT':
						setTopUpLimit('debit');
						break;

					case 'TRANSACTION_REJECTED':
						setPaymentRejected(true);
						break;

					default:
						if (!!error.response.details) {
							setTopUpError(error.response.details);
						} else {
							dispatch(
								setToast({
									error: true,
									label: translate('error.occured'),
									actionType: translate('error'),
								})
							);
						}

						break;
				}
			}
		);
	};

	useEffect(() => {
		setLoading((loading) => ({ ...loading, numbers: true }));
		AccountsService.getCorpUserNumbersWithChilds()
			.pipe(
				finalize(() => {
					setLoading((loading) => ({ ...loading, numbers: false }));
				})
			)
			.subscribe(({ accounts: numbers }) => {
				setNumbers(() => numbers.filter(({ isPrepaid }) => isPrepaid));
			});
	}, [dispatch]);

	useEffect(() => {
		if (topupRef.current) {
			setSelectedNumbers((selectedNumbers) => {
				return selectedNumbers.map((number) => ({
					...number,
					topUpValue: topupValue,
				}));
			});
		} else {
			topupRef.current = true;
		}
	}, [topupValue]);

	useEffect(() => {
		if (isConfirmationDialog && selectedNumbers.length === 0 && savedSelectedNumbers.length !== 0) {
			setSelectedNumbers(_.cloneDeep(savedSelectedNumbers));
		}
	}, [isConfirmationDialog, selectedNumbers, savedSelectedNumbers]);

	useEffect(() => {
		if (!(phoneNumberEncoded && (!foundNumber || selectedNumbers.length === 0))) {
			if (isSuccess || isError) {
				PaymentService.invalidateCacheAfterPayment('TOP_UP');
			}
		}
	}, [phoneNumberEncoded, foundNumber, selectedNumbers, isSuccess, isError]);

	useEffect(() => {
		if (!showDonations) {
			setSelectedDonation(null);
		}
	}, [showDonations]);

	return (
		<div style={{ width: '100%' }}>
			<ConditionalRender show={selectedNumbers.length > 0} onTrue={() => <NavigationPromptDialog />} />
			<BreadcrumbsItem to="/payments/topup">
				{`${translate('path.my.payments')} - ${translate('path.topup')}`}
			</BreadcrumbsItem>
			<ConditionalRender
				show={isConfirmationDialog}
				onTrue={() => (
					<PaymentConfirmationDialog
						hardReset={hardReset}
						isOpen
						isError={isError}
						topUpValue={topUpValue}
						donationValue={Number(selectedDonation?.value || 0)}
					/>
				)}
			/>
			<ConditionalRender
				show={paymentRejected}
				onTrue={() => <PaymentRejectedDialog isOpen hideDialog={() => setPaymentRejected(false)} />}
			/>
			<TabsWithDots
				activeTab={activeTab}
				setActiveTab={setActiveTab}
				tabs={[
					{
						name: translate('payments.topup.tabs.NumbersValue'),
						content: (
							<NumbersValue
								showTable={selectedNumbers.length > 0}
								showDialog={dialog === 'addNumbers' && !loading.numbers}
								handleTopUp={() => setActiveTab(Tabs.DONATION)}
								topupValue={topupValue}
								handleAddNumber={handleAddNumber}
								setTopupValue={setTopupValue}
								totalAmount={totalAmount}
								selectedNumbers={selectedNumbers}
								setSelectedNumbers={setSelectedNumbers}
								closeDialog={closeDialog}
								handleAdd={(numbers: Array<SingleAccount>) =>
									setSelectedNumbers((selectedNumbers) =>
										_.uniqBy(
											[
												...selectedNumbers,
												...numbers.map((number) => ({
													...number,
													topUpValue: topupValue,
													new: true,
												})),
											],
											'accountNum'
										)
									)
								}
								possibleNumbers={_.differenceBy(numbers, selectedNumbers, 'accountNum')}
							/>
						),
					},
					{
						name: translate('payments.topup.tabs.Donation'),
						content: (
							<Donation
								showDonations={showDonations}
								onChangeShowDonations={() => setShowDonations((showDonations) => !showDonations)}
								selectedDonation={selectedDonation}
								setSelectedDonation={setSelectedDonation}
								onBack={() => setActiveTab(Tabs.NUMBERS_VALUE)}
								onNext={() => setActiveTab(Tabs.PAYMENT)}
							/>
						),
					},
					{
						name: translate('payments.topup.tabs.Payment'),
						content: (
							<ConditionalRender
								show={dialog === 'paymentFrame'}
								onTrue={() => <PaymentFrame />}
								onFalse={() => (
									<Payment
										totalAmount={totalAmount}
										formData={formData}
										onBack={() => setActiveTab(Tabs.DONATION)}
										selectedDonation={selectedDonation}
										setFormCardData={setFormCardData}
										topUpError={topUpError}
										checkValidity={checkValidity}
										setCheckValidity={setCheckValidity}
										selectedNumbers={selectedNumbers}
										topUpLimit={topUpLimit}
										handleTopUp={handleTopUp}
										showForm={loading.payment}
										setTokenData={setToken}
										toggleSaveCard={toggleSaveCard}
										setSaveCard={setSaveCard}
									/>
								)}
							/>
						),
					},
				]}
			/>
		</div>
	);
};

export default TopUp;
